import { supabase } from '../../../../lib/supabase';
import { Client } from '../../../../context/ClientContext';

export interface MediaFile {
  id: string;
  client_id: string;
  bucket: string;
  object_key: string;
  mime_type: string;
  size_bytes: number;
  width_px?: number | null;
  height_px?: number | null;
  alt_text?: string | null;
  caption?: string | null;
  uploaded_by: string | null;
  created_at: string;
}

// File constraints for media
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB for media files
export const ALLOWED_MEDIA_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo',
  'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4',
  'application/pdf'
];

// Helper function to check if R2 bucket is configured for client
export const checkBucketExists = async (client: Client | undefined): Promise<boolean> => {
  if (!client) return false;
  
  try {
    // Check if client has media_domain configured
    // This indicates that R2 bucket and custom domain are set up
    return !!(client.media_domain && client.media_domain.trim());
  } catch (err) {
    // TODO: Consider upgrading to console.error for exception logging
    console.log('Bucket check exception:', err);
    return false;
  }
};

// Fetch media files for a client
export const fetchMediaFiles = async (clientId: string): Promise<MediaFile[]> => {
  const { data, error } = await supabase
    .from('media_assets')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

// Format file size helper
export const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return 'Unknown size';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
};

// Validate file helper
export const validateFile = (file: File): string | null => {
  if (file.size > MAX_FILE_SIZE) {
    return `File size must be less than ${formatFileSize(MAX_FILE_SIZE)}`;
  }
  
  if (!ALLOWED_MEDIA_TYPES.includes(file.type)) {
    return 'Only image, video, audio, and PDF files are allowed';
  }
  
  return null;
};

// Validate image URL helper
export const validateImageUrl = (url: string): string | null => {
  try {
    new URL(url);
    return null;
  } catch {
    return 'Please enter a valid URL';
  }
};

// Get media icon helper
export const getMediaIcon = (type: string | null) => {
  if (!type) return '📄';

  if (type.startsWith('image/')) return '🖼️';
  if (type.startsWith('video/')) return '🎥';
  if (type.startsWith('audio/')) return '🎵';
  if (type === 'application/pdf') return '📄';

  return '📄';
};

// Get media URL helper
export const getMediaUrl = (file: MediaFile, client?: Client): string => {
  return client?.media_domain 
    ? `https://${client.media_domain}/${file.object_key}`
    : `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/${file.bucket}/${file.object_key}`;
};